import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import StatusChip from "../../components/StatusChip";
import CompactPreview from "../../components/CompactPreview";

import { tokens } from "../../theme/tokens";
import {
  approvalStatusTone,
} from "../../status/StatusChipTones";

import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../auth/AuthContext";
import { useSOPApprovals } from "../../hooks/useSOPApprovals";
import useGraphQLClient from "../../hooks/useGraphQLClient";

import {
  READ_USERS_BY_TENANT_ID,
} from "../../graph/users/queries";

const approvalStatusLabel = (status) => {
  const labels = {
    PENDING: "Awaiting review",
    PENDING_APPROVAL: "Awaiting review",
    APPROVED: "Approved",
    REJECTED: "Rejected",
  };

  return labels[status] || status || "Awaiting review";
};

const getStaffUserLabel = (staffUser) => {
  const fullName = [
    staffUser?.firstName,
    staffUser?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    fullName ||
    staffUser?.email ||
    staffUser?.id ||
    "Unknown user"
  );
};

export default function TenantAdminSOPApprovalsView() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const client = useGraphQLClient();

  const { setSnackbar } = useSnackbar();

  const {
    tenantId: selectedTenantId,
    tenantName,
  } = useTenant();

  const { user } = useAuth();

  const tenantId =
    selectedTenantId ||
    user?.tenantID ||
    user?.tenantId;

  const [staffUsers, setStaffUsers] =
    useState([]);

  const [
    selectedApproversBySopId,
    setSelectedApproversBySopId,
  ] = useState({});

  const {
    adminApprovalSOPs,
    loading,
    error,
    assignSOPApprovers,
  } = useSOPApprovals({ tenantId });

  const sopsNeedingApprovers = useMemo(
    () =>
      adminApprovalSOPs.filter(
        (sop) =>
          (sop.approvalAssignments || [])
            .length === 0
      ),
    [adminApprovalSOPs]
  );

  const sopsAwaitingReview = useMemo(
    () =>
      adminApprovalSOPs.filter(
        (sop) =>
          (sop.approvalAssignments || [])
            .length > 0
      ),
    [adminApprovalSOPs]
  );

  useEffect(() => {
    const loadStaffUsers = async () => {
      if (!tenantId) {
        setStaffUsers([]);
        return;
      }

      try {
        const {
          readUsersByTenantID,
        } = await client.request(
          READ_USERS_BY_TENANT_ID,
          {
            tenantID: tenantId,
          }
        );

        setStaffUsers(
          (readUsersByTenantID || []).filter(
            (staffUser) =>
              staffUser.role === "TENANT_STAFF" &&
              !staffUser.archivedAt
          )
        );
      } catch (err) {
        console.error(
          "Failed to load tenant staff users",
          err
        );

        setStaffUsers([]);

        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load tenant staff",
        });
      }
    };

    loadStaffUsers();
  }, [
    client,
    tenantId,
    setSnackbar,
  ]);

  useEffect(() => {
    const savedApproversBySopId = {};

    adminApprovalSOPs.forEach((sop) => {
      savedApproversBySopId[sop.id] =
        (sop.approvalAssignments || []).map(
          (assignment) =>
            assignment.approverUserId
        );
    });

    setSelectedApproversBySopId(
      savedApproversBySopId
    );
  }, [adminApprovalSOPs]);

  const handleApproverChange = (
    sopId,
    approverUserIds
  ) => {
    setSelectedApproversBySopId((previous) => ({
      ...previous,
      [sopId]: approverUserIds,
    }));
  };

  const handleAssignApprovers = async (
    sopId
  ) => {
    const approverUserIds =
      selectedApproversBySopId[sopId] || [];

    if (approverUserIds.length === 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Select at least one approver",
      });

      return;
    }

    try {
      await assignSOPApprovers(
        sopId,
        approverUserIds
      );

      setSnackbar({
        open: true,
        severity: "success",
        message: "SOP approvers updated",
      });
    } catch (err) {
      console.error(
        "Failed to update SOP approvers",
        err
      );

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update SOP approvers",
      });
    }
  };

  const getApproverNames = (sop) => {
    const approverIds =
      (sop.approvalAssignments || []).map(
        (assignment) =>
          assignment.approverUserId
      );

    return approverIds.map((approverId) => {
      const staffUser = staffUsers.find(
        (candidate) =>
          candidate.id === approverId
      );

      return {
        id: approverId,
        label: staffUser
          ? getStaffUserLabel(staffUser)
          : "Assigned user",
      };
    });
  };

  const renderSopCard = (
    sop,
    hasExistingApprovers
  ) => {
    const selectedApprovers =
      selectedApproversBySopId[sop.id] || [];

    const assignedApprovers =
      getApproverNames(sop);

    return (
      <Paper
        key={sop.id}
        sx={{
          p: 2,
          border: `1px solid ${colors.scalpelTeal[400]}`,
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
          flexWrap="wrap"
          sx={{ mb: 1.5 }}
        >
          <Typography
            variant="h6"
            sx={{
              lineHeight: 1.4,
              fontWeight: 700,
              color: colors.scalpelTeal[400],
            }}
          >
            {sop.title}
          </Typography>

          <StatusChip
            label={
              hasExistingApprovers
                ? "Approver assigned"
                : "Needs approver"
            }
            tone={
              hasExistingApprovers
                ? "teal"
                : "danger"
            }
          />
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1.5 }}
        >
         <CompactPreview lines={8}>
            {sop.description || "No description"}
          </CompactPreview>
        </Typography>

        <Box
          display="flex"
          gap={0.75}
          flexWrap="wrap"
          sx={{ mb: 1.5 }}
        >
          <StatusChip
            label={approvalStatusLabel(
              sop.approvalStatus
            )}
            tone={approvalStatusTone(
              sop.approvalStatus
            )}
          />

          {sop.sopRoleAssignments?.map(
            (assignment) => (
              <StatusChip
                key={assignment.id}
                label={
                  assignment.tenantRole?.name ||
                  "Linked role"
                }
                tone="default"
              />
            )
          )}
        </Box>

        {hasExistingApprovers && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: colors.surface.card,
              border: `1px solid ${colors.surface.border}`,
            }}
          >
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: "block",
                mb: 0.75,
              }}
            >
              Assigned to
            </Typography>

            <Box
              display="flex"
              gap={0.75}
              flexWrap="wrap"
            >
              {assignedApprovers.map(
                (approver) => (
                  <StatusChip
                    key={approver.id}
                    label={approver.label}
                    tone="teal"
                  />
                )
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ mt: "auto" }}>
          <TextField
            select
            fullWidth
            size="small"
            label={
              hasExistingApprovers
                ? "Update approvers"
                : "Select approvers"
            }
            value={selectedApprovers}
            SelectProps={{
              multiple: true,
            }}
            onChange={(event) => {
              const value = event.target.value;

              handleApproverChange(
                sop.id,
                typeof value === "string"
                  ? value.split(",")
                  : value
              );
            }}
            sx={{ mb: 2 }}
          >
            {staffUsers.map((staffUser) => (
              <MenuItem
                key={staffUser.id}
                value={staffUser.id}
              >
                {getStaffUserLabel(staffUser)}
              </MenuItem>
            ))}
          </TextField>

          <Box
            display="flex"
            justifyContent="flex-end"
          >
            <ActionButton
              tone="gold"
              onClick={() =>
                handleAssignApprovers(sop.id)
              }
            >
              {hasExistingApprovers
                ? "Update Approvers"
                : "Assign Approvers"}
            </ActionButton>
          </Box>
        </Box>
      </Paper>
    );
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="MANAGE SOP APPROVERS"
          subtitle="Tenant could not be resolved"
        />

        <Typography mt={3}>
          No tenant is available for the current user.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={3}
      >
        <Header
          title="MANAGE SOP APPROVERS"
          subtitle={
            tenantName
              ? `Assign and monitor SOP approvers for ${tenantName}`
              : "Assign and monitor SOP approvers for your tenant"
          }
        />
      </Box>

      {loading ? (
        <Typography>
          Loading SOP approvals...
        </Typography>
      ) : error ? (
        <Typography color="error">
          Unable to load SOP approvals.
        </Typography>
      ) : adminApprovalSOPs.length === 0 ? (
        <Box mt={3}>
          <Typography variant="h6">
            There are no SOPs currently pending approval.
          </Typography>

          <Typography
            color="text.secondary"
            mt={1}
          >
            SOPs will appear here when they enter the
            approval workflow.
          </Typography>
        </Box>
      ) : (
        <>
          <ApprovalSection
            title="NEEDS AN APPROVER"
            count={sopsNeedingApprovers.length}
            description="These SOPs cannot progress until someone is assigned to review them."
            emptyMessage="Every pending SOP currently has an assigned approver."
            colors={colors}
          >
            {sopsNeedingApprovers.map((sop) =>
              renderSopCard(sop, false)
            )}
          </ApprovalSection>

          <Divider
            sx={{
              my: 4,
              borderColor: colors.surface.border,
            }}
          />

          <ApprovalSection
            title="ASSIGNED — AWAITING REVIEW"
            count={sopsAwaitingReview.length}
            description="An approver has been assigned. These SOPs are waiting for approval or rejection."
            emptyMessage="No assigned SOPs are currently awaiting review."
            colors={colors}
          >
            {sopsAwaitingReview.map((sop) =>
              renderSopCard(sop, true)
            )}
          </ApprovalSection>
        </>
      )}
    </Box>
  );
}

const ApprovalSection = ({
  title,
  count,
  description,
  emptyMessage,
  children,
  colors,
}) => (
  <Box component="section">
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      flexWrap="wrap"
      sx={{ mb: 0.5 }}
    >
      <Typography
        variant="h4"
        color="primary"
      >
        {title}
      </Typography>

      <StatusChip
        label={String(count)}
        tone={count > 0 ? "gold" : "default"}
      />
    </Box>

    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mb: 2 }}
    >
      {description}
    </Typography>

    {count === 0 ? (
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          border: `1px dashed ${colors.surface.border}`,
          bgcolor: colors.surface.card,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
        >
          {emptyMessage}
        </Typography>
      </Box>
    ) : (
      <Box
        display="grid"
        gap={2}
        gridTemplateColumns="repeat(auto-fill, minmax(360px, 1fr))"
        alignItems="stretch"
      >
        {children}
      </Box>
    )}
  </Box>
);